// blogData.js
// Optimized blog data with global styles

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

const calculateReadingTime = (content) => {
  const text = content.replace(/<[^>]*>/g, "");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.ceil(words / 200);
};

const generateExcerpt = (content, maxLength = 160) => {
  const text = content.replace(/<[^>]*>/g, "").trim();
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

// ============================================================
// POST 1: SPACEX GOPHISH CONTENT (SIMPLIFIED)
// ============================================================
const spacexGophishContent = `
  <div class="blog-content">
    <!-- PROJECT OVERVIEW -->
    <div class="blog-card">
      <h2 class="blog-h2 text-emerald">📋 Project Overview</h2>
      <div class="blog-grid-2">
        <div>
          <p class="blog-text-sm"><strong class="text-blue">Objective:</strong> Create phishing awareness program for SpaceX propulsion engineers</p>
          <p class="blog-text-sm"><strong class="text-blue">Tools:</strong> Gophish, MailHog, Docker, QEMU (ARM64 compatibility)</p>
        </div>
        <div>
          <p class="blog-text-sm"><strong class="text-blue">Duration:</strong> Full implementation (from zero to working demo)</p>
          <p class="blog-text-sm"><strong class="text-blue">Final Result:</strong> Working phishing simulation with credential capture</p>
        </div>
      </div>
    </div>

    <!-- PHASE 1 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-blue">🚀 PHASE 1: ENVIRONMENT SETUP</h2>
      
      <h3 class="blog-h3 text-purple">Step 1.1: System Preparation</h3>
      <div class="blog-mt-4">
        <pre class="blog-pre"><code><span class="comment"># Update Kali Linux</span>
sudo apt update && sudo apt upgrade -y

<span class="comment"># Install Docker (if not present)</span>
sudo apt install docker.io docker-compose -y

<span class="comment"># Add user to docker group</span>
sudo usermod -aG docker $USER
newgrp docker  <span class="comment"># Reload group permissions</span>

<span class="comment"># Install QEMU for ARM64 compatibility</span>
sudo apt install qemu-user-static binfmt-support -y</code></pre>
      </div>

      <h3 class="blog-h3 text-purple">Step 1.2: Create Project Directory</h3>
      <div class="blog-mt-4">
        <pre class="blog-pre"><code>mkdir ~/spacex-security-project
cd ~/spacex-security-project
mkdir gophish-setup
cd gophish-setup</code></pre>
      </div>

      <h3 class="blog-h3 text-purple">Step 1.3: Download Gophish (ARM64 Workaround)</h3>
      <div class="blog-mt-4">
        <pre class="blog-pre"><code><span class="comment"># Download Gophish AMD64 binary (will use QEMU emulation)</span>
wget https://github.com/gophish/gophish/releases/download/v0.12.1/gophish-v0.12.1-linux-64bit.zip

<span class="comment"># Extract</span>
unzip gophish-v0.12.1-linux-64bit.zip

<span class="comment"># Make executable</span>
chmod +x gophish</code></pre>
      </div>

      <div class="blog-alert-warning">
        <p class="blog-text-sm"><strong class="text-red">⚠️ CRITICAL NOTE:</strong> We use QEMU because Gophish doesn't have ARM64 binary. On AMD64 systems, just run <code>./gophish</code> directly.</p>
      </div>
    </div>

    <!-- PHASE 2 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-purple">🛠️ PHASE 2: Gophish Deployment</h2>
      
      <h3 class="blog-h3 text-teal">Step 2.1: First Launch & Password Capture</h3>
      <div class="blog-mt-4">
        <pre class="blog-pre"><code><span class="comment"># Launch Gophish with QEMU emulation</span>
qemu-x86_64 -L /usr/x86_64-linux-gnu ./gophish &</code></pre>
      </div>

      <div class="blog-code-block">
        <p class="blog-text-sm"><strong class="text-emerald">EXPECTED OUTPUT:</strong></p>
        <pre class="blog-text-sm blog-mt-2"><code>time="..." level=info msg="Please login with the username admin and the password XXXXX"
time="..." level=info msg="Starting admin server at https://127.0.0.1:3333"
time="..." level=info msg="Starting phishing server at http://0.0.0.0:80"</code></pre>
      </div>

      <div class="blog-alert-warning">
        <p class="blog-text-sm"><strong class="text-red">⚠️ IMPORTANT:</strong> Save the admin password shown! Example: <code>8953e35993520c5e</code></p>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 13.16.18.png" alt="Terminal showing Gophish startup with password" class="blog-image" />

      <h3 class="blog-h3 text-teal">Step 2.2: Initial Configuration</h3>
      <ol class="blog-list-disc blog-text-sm">
        <li><strong class="text-blue">Access Admin Panel:</strong> Open browser to <code>https://127.0.0.1:3333</code></li>
        <li>
          <strong class="text-blue">Login:</strong>
          <ul class="blog-list-disc">
            <li>Username: <code>admin</code></li>
            <li>Password: (from Step 2.1 output)</li>
          </ul>
        </li>
        <li>
          <strong class="text-blue">Change Password:</strong>
          <ul class="blog-list-disc">
            <li>Current: <code>gophish</code> (or password from startup)</li>
            <li>New: Choose strong password (e.g., <code>SpaceX-Raptor2024!</code>)</li>
            <li>Confirm: Same password</li>
            <li>Click: <strong class="text-teal">Save Password</strong></li>
          </ul>
        </li>
      </ol>

      <img src="/blog/images/Screenshot 2026-06-29 at 13.23.38.png" alt="Gophish login screen" class="blog-image" />
      <img src="/blog/images/Screenshot 2026-06-29 at 13.25.40.png" alt="Password change screen" class="blog-image" />
    </div>

    <!-- PHASE 3 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-emerald">📧 PHASE 3: MailHog Setup for Email Testing</h2>
      
      <h3 class="blog-h3 text-teal">Step 3.1: Deploy MailHog Container</h3>
      <div class="blog-mt-4">
        <pre class="blog-pre"><code><span class="comment"># Run MailHog container (SMTP testing server)</span>
docker run -d -p 8025:8025 -p 1025:1025 --name mailhog mailhog/mailhog:latest

<span class="comment"># Verify it's running</span>
docker ps</code></pre>
      </div>

      <div class="blog-code-block">
        <p class="blog-text-sm"><strong class="text-emerald">Expected output:</strong></p>
        <pre class="blog-text-sm blog-mt-2"><code>CONTAINER ID   IMAGE                    STATUS         PORTS
xxxxxxxxxxxx   mailhog/mailhog:latest   Up 2 seconds   0.0.0.0:1025->1025/tcp, 0.0.0.0:8025->8025/tcp</code></pre>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 13.36.11.png" alt="Docker ps showing MailHog running" class="blog-image" />

      <h3 class="blog-h3 text-teal">Step 3.2: Configure Gophish Sending Profile</h3>
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">In Gophish Web Interface:</strong></p>
        <ol class="blog-list-decimal blog-text-sm">
          <li>Navigate to: <strong class="text-blue">Sending Profiles</strong> → <strong class="text-blue">New Profile</strong></li>
          <li>
            Fill exactly:
            <div class="blog-code-block blog-mt-2">
              <pre class="blog-text-xs"><code>Name: MailHog Test
Host: 127.0.0.1
SMTP From: collab.verify@spacex-internal.net

Username: (leave blank)
Password: (leave blank)
Port: 25 (select from "Show" dropdown)</code></pre>
            </div>
          </li>
          <li><strong class="text-blue">UNCHECK:</strong> "Ignore Certificate Errors"</li>
          <li>Click: <strong class="text-teal">Save Sending Profile</strong></li>
        </ol>
      </div>

      <div class="blog-alert-warning">
        <p class="blog-text-sm"><strong class="text-red">⚠️ CRITICAL:</strong> Port must be <code>25</code> (dropdown selection), MailHog will map to 1025.</p>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 14.30.13.png" alt="Sending profile configuration" class="blog-image" />
    </div>

    <!-- PHASE 4 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-orange">🎨 PHASE 4: Create SpaceX Phishing Templates</h2>
      
      <h3 class="blog-h3 text-orange">Step 4.1: Email Template Creation</h3>
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">In Gophish:</strong> Email Templates → New Template</p>
        <div class="blog-text-sm">
          <p><strong class="text-blue">Name:</strong> <code>SpaceX Raptor CAD Request</code></p>
          <p><strong class="text-blue">Subject:</strong> <code>Action Required: Raptor 2 Nozzle CAD Files - URGENT</code></p>
        </div>
      </div>

      <div class="blog-mt-4">
        <pre class="blog-pre"><code>&lt;!DOCTYPE html&gt;
&lt;html&gt;
&lt;body style="font-family: Arial, sans-serif; color: #333;"&gt;
  &lt;div style="max-width: 600px; margin: 0 auto; border: 1px solid #e0e0e0;"&gt;
    &lt;div style="background: #000; color: white; padding: 15px; text-align: center;"&gt;
      &lt;strong&gt;SPACEX INTERNAL COLLABORATION&lt;/strong&gt;
    &lt;/div&gt;
    &lt;div style="padding: 25px;"&gt;
      &lt;p&gt;Hello Propulsion Team,&lt;/p&gt;
      &lt;p&gt;This is Dr. Chen from the &lt;strong&gt;Advanced Materials Research Institute (AMRI)&lt;/strong&gt;...&lt;/p&gt;
    &lt;/div&gt;
  &lt;/div&gt;
&lt;/body&gt;
&lt;/html&gt;</code></pre>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 13.57.09.png" alt="Email template editor" class="blog-image" />

      <h3 class="blog-h3 text-orange">Step 4.2: Landing Page Creation</h3>
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">In Gophish:</strong> Landing Pages → New Page</p>
        <p class="blog-text-sm"><strong class="text-blue">Name:</strong> <code>AMRI SpaceX Secure Portal</code></p>
      </div>

      <div class="blog-card blog-mt-4">
        <p class="blog-text-sm"><strong class="text-emerald">Critical Settings:</strong></p>
        <ul class="blog-list-disc blog-text-sm">
          <li class="blog-flex-center">✅ <strong class="text-blue">Capture Submitted Data:</strong> CHECKED</li>
          <li class="blog-flex-center">✅ <strong class="text-blue">Capture Passwords:</strong> CHECKED</li>
          <li>→ <strong class="text-blue">Redirect to:</strong> <code>https://www.spacex.com/</code></li>
          <li>→ Click: <strong class="text-orange">Save Page</strong></li>
        </ul>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 14.04.21.png" alt="Landing page editor" class="blog-image" />
    </div>

    <!-- PHASE 5 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-pink">👥 PHASE 5: Create Test Users</h2>
      
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">In Gophish:</strong> Users & Groups → New Group</p>
        <p class="blog-text-sm"><strong class="text-blue">Group Name:</strong> <code>SpaceX Propulsion Engineers - Test</code></p>
      </div>

      <div class="blog-mt-4">
        <pre class="blog-pre"><code>First Name,Last Name,Email
John,Smith,john.smith@test.spacex
Sarah,Chen,sarah.chen@test.spacex
Mark,Johnson,mark.johnson@test.spacex
Elena,Rodriguez,elena.rodriguez@test.spacex
David,Kim,david.kim@test.spacex</code></pre>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 14.12.06.png" alt="User group" class="blog-image" />
    </div>

    <!-- PHASE 6 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-indigo">🎯 PHASE 6: Launch Campaign</h2>
      
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">In Gophish:</strong> Campaigns → New Campaign</p>
        <div class="blog-code-block blog-mt-2">
          <pre class="blog-text-xs"><code>Name: SpaceX Raptor CAD Phishing Test
Email Template: SpaceX Raptor CAD Request
Landing Page: AMRI SpaceX Secure Portal
URL: http://localhost:80
Sending Profile: MailHog Test
Groups: SpaceX Propulsion Engineers - Test
Launch Date: Send emails immediately</code></pre>
        </div>
        <p class="blog-text-sm blog-mt-2"><strong class="text-blue">Click:</strong> Launch Campaign</p>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 14.14.57.png" alt="Campaign creation" class="blog-image" />
      <img src="/blog/images/Screenshot 2026-06-29 at 14.36.11.png" alt="MailHog showing emails" class="blog-image" />
    </div>

    <!-- PHASE 7 -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-red">🧪 PHASE 7: Test Credential Capture</h2>
      
      <div class="blog-card">
        <p class="blog-text-sm"><strong class="text-emerald">On Landing Page (http://localhost:80):</strong></p>
        <ol class="blog-list-decimal blog-text-sm">
          <li><strong class="text-blue">Email field:</strong> Enter <code>test@spacex.com</code></li>
          <li><strong class="text-blue">Password field:</strong> Enter <code>test123</code></li>
          <li>Click: <strong class="text-red">🔐 AUTHENTICATE & CONTINUE</strong></li>
        </ol>
      </div>

      <div class="blog-code-block">
        <p class="blog-text-sm"><strong class="text-emerald">Expected Result:</strong></p>
        <ul class="blog-list-disc blog-text-sm">
          <li>Page redirects to <code>https://www.spacex.com/</code></li>
          <li>Credentials captured in Gophish</li>
        </ul>
      </div>

      <img src="/blog/images/Screenshot 2026-06-29 at 14.39.41.png" alt="Landing page" class="blog-image" />
      <img src="/blog/images/Screenshot 2026-06-29 at 14.41.35.png" alt="Campaign results" class="blog-image" />
    </div>

    <!-- PHASE 8: Troubleshooting -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-teal">🔧 PHASE 8: Troubleshooting Solutions</h2>
      
      <h3 class="blog-h3 text-teal">Problem 1: "Port already in use" error</h3>
      <pre class="blog-pre"><code><span class="comment"># Find process using port 3333 or 80</span>
sudo netstat -tulpn | grep :3333
sudo netstat -tulpn | grep :80
sudo kill -9 &lt;PID&gt;</code></pre>

      <h3 class="blog-h3 text-teal">Problem 2: Emails not sending</h3>
      <pre class="blog-pre"><code>docker stop mailhog
docker rm mailhog
docker run -d -p 8025:8025 -p 25:1025 --name mailhog mailhog/mailhog:latest</code></pre>

      <h3 class="blog-h3 text-teal">Problem 3: Submitted Data = 0</h3>
      <div class="blog-code-block">
        <p class="blog-text-sm"><strong class="text-emerald">Solution:</strong> Check landing page HTML</p>
        <ul class="blog-list-disc blog-text-sm">
          <li>Form must have: <code>&lt;form method="post"&gt;</code></li>
          <li>Inputs must have: <code>name="username"</code> and <code>name="password"</code></li>
          <li><strong class="text-blue">Capture Submitted Data</strong> = CHECKED</li>
        </ul>
      </div>

      <h3 class="blog-h3 text-teal">Problem 4: ARM64 "exec format error"</h3>
      <pre class="blog-pre"><code>sudo apt install qemu-user-static -y
qemu-x86_64 -L /usr/x86_64-linux-gnu ./gophish</code></pre>
    </div>

    <!-- PRO TIPS -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-emerald">💡 PRO TIPS & BEST PRACTICES</h2>
      
      <div class="blog-grid-3">
        <div class="blog-card">
          <h3 class="text-blue blog-font-semibold">Email Template Design</h3>
          <ol class="blog-list-decimal blog-text-sm">
            <li><strong class="text-purple">Urgency:</strong> "Action required by EOD"</li>
            <li><strong class="text-purple">Authority:</strong> Use familiar names</li>
            <li><strong class="text-purple">Relevance:</strong> Target-specific content</li>
          </ol>
        </div>

        <div class="blog-card">
          <h3 class="text-green blog-font-semibold">Landing Page Optimization</h3>
          <ol class="blog-list-decimal blog-text-sm">
            <li><strong class="text-purple">Loading Speed:</strong> &lt; 3 seconds</li>
            <li><strong class="text-purple">Mobile Responsive:</strong> Test on phones</li>
            <li><strong class="text-purple">SSL/HTTPS:</strong> Essential for credibility</li>
          </ol>
        </div>

        <div class="blog-card">
          <h3 class="text-purple blog-font-semibold">Campaign Management</h3>
          <ol class="blog-list-decimal blog-text-sm">
            <li><strong class="text-purple">Schedule:</strong> Send during work hours</li>
            <li><strong class="text-purple">Frequency:</strong> 1-2 campaigns per month</li>
            <li><strong class="text-purple">Analysis:</strong> Review results, adjust strategy</li>
          </ol>
        </div>
      </div>
    </div>

    <!-- COMPLETION CHECKLIST -->
    <div class="blog-divider">
      <h2 class="blog-h1 text-emerald">✅ COMPLETION CHECKLIST</h2>
      <div class="blog-grid-2">
        <ul class="blog-list-disc blog-text-sm">
          <li>✓ Gophish installed and running</li>
          <li>✓ MailHog configured</li>
          <li>✓ SpaceX email template created</li>
          <li>✓ Landing page with credential capture</li>
        </ul>
        <ul class="blog-list-disc blog-text-sm">
          <li>✓ Test user group created</li>
          <li>✓ Campaign launched successfully</li>
          <li>✓ Credentials captured and visible</li>
          <li>✓ Metrics recorded</li>
        </ul>
      </div>
    </div>

    <!-- CONCLUSION -->
    <div class="blog-card blog-text-center blog-mt-8">
      <p class="blog-font-medium">✅ Project successfully implemented. Ready for demonstration and production deployment with proper authorization.</p>
    </div>
  </div>
`;

// ============================================================
// POST 2: JWT AUTHENTICATION CONTENT
// ============================================================
const jwtContent = `
  <div class="blog-content">
    <div class="blog-card">
      <h2 class="blog-h2 text-emerald">📋 Overview</h2>
      <p class="blog-text-sm">A deep dive into JSON Web Tokens, how they work, and best practices for implementing secure authentication in your applications.</p>
    </div>

    <div class="blog-divider">
      <h2 class="blog-h1 text-blue">🔐 Understanding JWT Structure</h2>
      <div class="blog-code-block">
        <pre class="blog-pre"><code>Header.Payload.Signature</code></pre>
      </div>
      <p class="blog-text-sm">A JWT consists of three parts: Header, Payload, and Signature. Each part is Base64Url encoded and separated by dots.</p>
    </div>

    <div class="blog-divider">
      <h2 class="blog-h1 text-purple">🏗️ Implementation</h2>
      <pre class="blog-pre"><code><span class="comment"># Example JWT implementation in Node.js</span>
const jwt = require('jsonwebtoken');
const token = jwt.sign({ userId: 123 }, 'secret-key', { expiresIn: '1h' });</code></pre>
    </div>

    <div class="blog-divider">
      <h2 class="blog-h1 text-red">⚠️ Security Considerations</h2>
      <ul class="blog-list-disc blog-text-sm">
        <li>Always use HTTPS</li>
        <li>Store tokens securely (HttpOnly cookies)</li>
        <li>Implement token rotation</li>
        <li>Use short expiration times</li>
        <li>Validate the signature on every request</li>
      </ul>
    </div>

    <div class="blog-card blog-text-center blog-mt-8">
      <p class="blog-font-medium">✅ Learn more about secure JWT implementation in modern web applications.</p>
    </div>
  </div>
`;

// ============================================================
// POST 3: GAME OF ACTIVE DIRECTORY CONTENT (SIMPLIFIED)
// ============================================================
const goadContent = `
  <div class="blog-content">
    <!-- TABLE OF CONTENTS -->
    <div class="blog-card blog-card-light">
      <h2 class="blog-h2 text-emerald">📑 Table of Contents</h2>
      <ul class="blog-list-disc blog-text-sm">
        <li><a href="#overview" class="blog-link">🎯 Project Overview</a></li>
        <li><a href="#objectives" class="blog-link">🎓 Learning Objectives</a></li>
        <li><a href="#architecture" class="blog-link">🏗️ Lab Architecture</a></li>
        <li><a href="#tech" class="blog-link">🧰 Technologies & Tools</a></li>
        <li><a href="#attack-path" class="blog-link">🎯 Attack Path</a></li>
        <li><a href="#narrative" class="blog-link">🔓 Attack Narrative</a></li>
        <li><a href="#blue-team" class="blog-link">🔵 Blue Team Investigation</a></li>
        <li><a href="#detection" class="blog-link">🚨 Detection Engineering</a></li>
        <li><a href="#hardening" class="blog-link">🛡️ Defensive Hardening</a></li>
        <li><a href="#mitre" class="blog-link">🎯 MITRE ATT&CK Mapping</a></li>
        <li><a href="#lessons" class="blog-link">📝 Lessons Learned</a></li>
      </ul>
    </div>

    <!-- OVERVIEW -->
    <div id="overview" class="blog-card">
      <h2 class="blog-h2 text-emerald">🎯 Project Overview</h2>
      <p class="blog-text-sm blog-mb-4">
        This project documents a full offensive-then-defensive engagement inside <strong class="text-blue">GOAD</strong>, an intentionally vulnerable, script-deployed Active Directory environment built to mirror real-world enterprise misconfigurations.
      </p>
      <p class="blog-text-sm">
        The goal was not just to "win" as an attacker, but to close the loop: reconstruct the intrusion purely from log evidence, prove that new detections actually fire, and apply the specific hardening changes that break each stage of the kill chain.
      </p>
    </div>

    <!-- LEARNING OBJECTIVES -->
    <div id="objectives" class="blog-divider">
      <h2 class="blog-h1 text-blue">🎓 Learning Objectives</h2>
      <ul class="blog-list-disc blog-text-sm">
        <li>Understand Active Directory trust structures and how they expand attack surface.</li>
        <li>Practice AD enumeration and credential-harvesting techniques.</li>
        <li>Chain privilege escalation techniques into full Domain Admin compromise.</li>
        <li>Reconstruct an attack timeline purely from SIEM telemetry.</li>
        <li>Author and validate custom Wazuh detection rules.</li>
        <li>Apply targeted hardening measures mapped directly to each exploited weakness.</li>
      </ul>
    </div>

    <!-- LAB ARCHITECTURE -->
    <div id="architecture" class="blog-divider">
      <h2 class="blog-h1 text-purple">🏗️ Lab Architecture</h2>
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>Host</th><th>Role</th><th>IP Address</th></tr>
          </thead>
          <tbody>
            <tr><td>ubuntu-jumpbox</td><td>SSH pivot</td><td>192.168.56.100</td></tr>
            <tr><td>kingslanding</td><td>Domain Controller</td><td>192.168.56.10</td></tr>
            <tr><td>winterfell</td><td>Domain Controller</td><td>192.168.56.11</td></tr>
            <tr><td>castelblack</td><td>Member Server</td><td>192.168.56.22</td></tr>
            <tr><td>goad-vm-wazuh</td><td>SIEM Server</td><td>192.168.56.51</td></tr>
          </tbody>
        </table>
      </div>
      <p class="blog-text-sm blog-mt-2">
        Domain: <strong class="text-emerald">sevenkingdoms.local</strong>, with child domain <strong class="text-emerald">north.sevenkingdoms.local</strong>
      </p>
    </div>

    <!-- TECHNOLOGIES -->
    <div id="tech" class="blog-divider">
      <h2 class="blog-h1 text-teal">🧰 Technologies & Tools</h2>
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>Category</th><th>Tools / Technologies</th></tr>
          </thead>
          <tbody>
            <tr><td>Operating Systems</td><td>Kali Linux, Windows Server, Ubuntu</td></tr>
            <tr><td>AD Enumeration</td><td>nmap, crackmapexec</td></tr>
            <tr><td>Exploitation</td><td>ASPX webshell, GodPotato</td></tr>
            <tr><td>Credential Access</td><td>mimikatz, reg save</td></tr>
            <tr><td>Lateral Movement</td><td>Pass-the-Hash, CrackMapExec</td></tr>
            <tr><td>Detection / SIEM</td><td>Wazuh Manager, custom rules</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ATTACK PATH -->
    <div id="attack-path" class="blog-divider">
      <h2 class="blog-h1 text-indigo">🎯 Attack Path</h2>
      <div class="blog-card-light blog-p-4">
        <pre class="blog-pre"><code>
┌─────────────────────────────────────────────────────────────────────┐
│  1. Initial Access → SSH pivot into ubuntu-jumpbox                 │
│  2. Network Enumeration → nmap scan → 5 live hosts                │
│  3. AD Enumeration → Kerberos, LDAP, SMB confirmed                │
│  4. Credential Discovery → grep in GOAD repo → plaintext found    │
│  5. SMB Enumeration → CrackMapExec → signing:False on castelblack │
│  6. Privilege Escalation → Harvested credentials = Domain Admin   │
│  7. Lateral Movement → Same credentials → child domain DC         │
│                                                                     │
│  ✅ Full domain compromise achieved                               │
└─────────────────────────────────────────────────────────────────────┘</code></pre>
      </div>
    </div>

    <!-- ATTACK NARRATIVE -->
    <div id="narrative" class="blog-divider">
      <h2 class="blog-h1 text-red">🔓 Attack Narrative</h2>
      
      <h3 class="blog-h3 text-red">1. Initial Access</h3>
      <pre class="blog-pre"><code>ssh -i jumpbox.pem goad@20.170.46.7</code></pre>
      <p class="blog-text-sm">Low-privileged user <strong class="text-blue">goad</strong> with sudo rights, internal IP <strong class="text-blue">192.168.56.100</strong>.</p>

      <h3 class="blog-h3 text-red">2. Network & Service Enumeration</h3>
      <pre class="blog-pre"><code>nmap -sn 192.168.56.0/24
nmap -sV -p 88,389,445,3389 192.168.56.10,11,22</code></pre>
      <p class="blog-text-sm">Domain confirmed: <strong class="text-emerald">sevenkingdoms.local</strong></p>

      <h3 class="blog-h3 text-red">3. Credential Discovery</h3>
      <pre class="blog-pre"><code>grep -rEi "password|user|admin" /home/goad/GOAD/</code></pre>
      <div class="blog-alert-danger">
        <p class="blog-text-sm"><strong class="text-red">🔴 Impact:</strong> Full domain credential compromise before a single exploit was run.</p>
      </div>

      <h3 class="blog-h3 text-red">4. Privilege Escalation & Lateral Movement</h3>
      <pre class="blog-pre"><code>crackmapexec smb 192.168.56.10 -u cersei.lannister -p &lt;password&gt;
crackmapexec smb 192.168.56.11 -u cersei.lannister -p &lt;password&gt;</code></pre>
      <p class="blog-text-sm">Authentication succeeded against both Domain Controllers — full compromise of <strong class="text-emerald">sevenkingdoms.local</strong> and <strong class="text-emerald">north.sevenkingdoms.local</strong>.</p>
    </div>

    <!-- BLUE TEAM -->
    <div id="blue-team" class="blog-divider">
      <h2 class="blog-h1 text-blue">🔵 Blue Team Investigation (Wazuh)</h2>
      
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>Question</th><th>Evidence Source</th><th>Finding</th></tr>
          </thead>
          <tbody>
            <tr><td>Suspicious PowerShell?</td><td>Event ID 4104</td><td class="text-red">Reverse shell confirmed</td></tr>
            <tr><td>Privilege escalation?</td><td>Event ID 4688</td><td class="text-red">GodPotato-style escalation</td></tr>
            <tr><td>Credentials dumped?</td><td>Process names, reg save</td><td class="text-red">Mimikatz-style dumping</td></tr>
            <tr><td>Lateral movement?</td><td>Event ID 4624, Logon Type 9</td><td class="text-red">Pass-the-Hash confirmed</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- DETECTION ENGINEERING -->
    <div id="detection" class="blog-divider">
      <h2 class="blog-h1 text-purple">🚨 Detection Engineering</h2>
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>Rule ID Range</th><th>Detection Focus</th></tr>
          </thead>
          <tbody>
            <tr><td>400010–400012</td><td>Suspicious PowerShell</td></tr>
            <tr><td>400020–400022</td><td>GodPotato execution</td></tr>
            <tr><td>400030–400035</td><td>Mimikatz execution</td></tr>
            <tr><td>400050–400054</td><td>Pass-the-Hash indicators</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- DEFENSIVE HARDENING -->
    <div id="hardening" class="blog-divider">
      <h2 class="blog-h1 text-emerald">🛡️ Defensive Hardening</h2>
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>#</th><th>Fix</th><th>Target</th><th>Technique Neutralized</th></tr>
          </thead>
          <tbody>
            <tr><td>1</td><td>Enforced 14-char password policy</td><td>winterfell</td><td>Credential reuse</td></tr>
            <tr><td>2</td><td>Removed Execute permission; web.config restrictions</td><td>castelblack</td><td>ASPX webshell</td></tr>
            <tr><td>3</td><td>Restricted IIS service account</td><td>castelblack</td><td>GodPotato escalation</td></tr>
            <tr><td>4</td><td>Set LocalAccountTokenFilterPolicy=0, SMB signing</td><td>castelblack, winterfell</td><td>Pass-the-Hash</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MITRE ATT&CK -->
    <div id="mitre" class="blog-divider">
      <h2 class="blog-h1 text-indigo">🎯 MITRE ATT&CK Mapping</h2>
      <div class="blog-table-wrapper">
        <table class="blog-table">
          <thead>
            <tr><th>Tactic</th><th>Technique</th><th>ID</th></tr>
          </thead>
          <tbody>
            <tr><td>Initial Access</td><td>Valid Accounts</td><td>T1078</td></tr>
            <tr><td>Reconnaissance</td><td>Network Service Discovery</td><td>T1046</td></tr>
            <tr><td>Credential Access</td><td>Unsecured Credentials</td><td>T1552.001</td></tr>
            <tr><td>Credential Access</td><td>OS Credential Dumping</td><td>T1003.001</td></tr>
            <tr><td>Privilege Escalation</td><td>Token Impersonation</td><td>T1134.001</td></tr>
            <tr><td>Lateral Movement</td><td>Pass the Hash</td><td>T1550.002</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- LESSONS LEARNED -->
    <div id="lessons" class="blog-divider">
      <h2 class="blog-h1 text-amber">📝 Lessons Learned</h2>
      
      <h3 class="blog-h3 text-amber">What worked for the attacker:</h3>
      <ul class="blog-list-disc blog-text-sm">
        <li>A single leaked credentials file collapsed the entire domain trust boundary.</li>
        <li>CrackMapExec enabled rapid validation of harvested credentials.</li>
        <li>The bidirectional domain trust let one compromised account pivot straight into the child domain.</li>
      </ul>

      <h3 class="blog-h3 text-amber">Where defenses were weakest:</h3>
      <ul class="blog-list-disc blog-text-sm">
        <li>No control prevented plaintext credentials from sitting in a local repository.</li>
        <li>SMB signing was disabled on a production-facing member server.</li>
        <li>Initial reconnaissance activity generated no detectable alerts.</li>
      </ul>

      <div class="blog-alert-danger">
        <p class="blog-text-sm blog-font-medium">
          🔑 Key takeaway: A single insecure file repository can collapse an Active Directory domain faster than a SOC can refresh its dashboard — detection engineering matters, but eliminating credential sprawl at the source matters more.
        </p>
      </div>
    </div>

    <!-- CONCLUSION -->
    <div class="blog-card blog-text-center blog-mt-8">
      <p class="blog-font-medium">✅ Complete offensive-to-defensive Active Directory engagement — from full domain compromise to SIEM-driven incident reconstruction and targeted hardening.</p>
    </div>
  </div>
`;

// ============================================================
// BLOG POSTS DATA
// ============================================================

export const blogPosts = [
  // ============================================================
  // POST 1: SpaceX Gophish Implementation
  // ============================================================
  {
    id: 1,
    title: "SpaceX Gophish Implementation: Complete Step-by-Step Guide",
    slug: "spacex-gophish-implementation-complete-guide",
    excerpt:
      "A comprehensive guide to setting up a phishing awareness program for SpaceX propulsion engineers using Gophish, MailHog, and Docker.",
    content: spacexGophishContent,
    author: "Wais Nazari",
    date: "2026-06-29",
    category: "Cybersecurity",
    tags: [
      "Cybersecurity",
      "Social Engineering",
      "Gophish",
      "Phishing",
      "SpaceX",
      "Penetration Testing",
    ],
    image: "/blog/images/COVER.jpg",
    readTime: null,
    likes: 0,
    comments: [],
    githubRepo: "https://github.com/Wais000/spacex-gophish-project",
    metaDescription:
      "Master phishing awareness with Gophish, MailHog, and Docker.",
    keywords: "Gophish, phishing simulation, cybersecurity, SpaceX",
    canonical:
      "https://yourportfolio.com/blog/spacex-gophish-implementation-complete-guide",
    published: true,
    featured: true,
  },

  // ============================================================
  // POST 2: JWT Authentication
  // ============================================================
  {
    id: 2,
    title: "Understanding JWT Authentication in Modern Web Applications",
    slug: "understanding-jwt-authentication-modern-web",
    excerpt:
      "A deep dive into JSON Web Tokens, how they work, and best practices for implementing secure authentication.",
    content: jwtContent,
    author: "Wais Nazari",
    date: "2026-06-25",
    category: "Web Development",
    tags: [
      "JWT",
      "Authentication",
      "Security",
      "Web Development",
      "JavaScript",
    ],
    image: "/blog/images/jwt-cover.png",
    readTime: null,
    likes: 0,
    comments: [],
    githubRepo: "https://github.com/Wais000/jwt-auth-example",
    metaDescription: "Learn how JWT authentication works and best practices.",
    keywords: "JWT, authentication, web security, OAuth2",
    canonical:
      "https://yourportfolio.com/blog/understanding-jwt-authentication-modern-web",
    published: true,
    featured: false,
  },

  // ============================================================
  // POST 3: Game of Active Directory
  // ============================================================
  {
    id: 3,
    title: "Game of Active Directory — Full Compromise & Detection Engineering",
    slug: "game-of-active-directory-full-compromise-detection-engineering",
    excerpt:
      "An end-to-end Red Team → Blue Team engagement against the GOAD lab: full domain compromise, Wazuh detection engineering, and infrastructure hardening.",
    content: goadContent,
    author: "Wais Nazari",
    date: "2026-07-02",
    category: "Cybersecurity",
    tags: [
      "Active Directory",
      "Red Team",
      "Blue Team",
      "Wazuh",
      "SIEM",
      "Detection Engineering",
      "GOAD",
    ],
    image: "/blog/images/Ad-cover.png",
    readTime: null,
    likes: 0,
    comments: [],
    githubRepo: "https://github.com/Wais000/game-of-active-directory",
    metaDescription:
      "Full compromise of GOAD Active Directory lab with Wazuh detection engineering.",
    keywords: "Active Directory, GOAD, Wazuh, SIEM, Detection Engineering",
    canonical:
      "https://yourportfolio.com/blog/game-of-active-directory-full-compromise-detection-engineering",
    published: true,
    featured: false,
  },

  // ============================================================
  // POST 4: ADD NEW POST HERE
  // ============================================================
  // {
  //   id: 4,
  //   title: "Your New Post Title",
  //   slug: "your-new-post-slug",
  //   excerpt: "Your post excerpt here...",
  //   content: yourNewPostContent,
  //   author: "Wais Nazari",
  //   date: "2026-07-XX",
  //   category: "Category Name",
  //   tags: ["Tag1", "Tag2"],
  //   image: "/blog/images/your-image.png",
  //   readTime: null,
  //   likes: 0,
  //   comments: [],
  //   githubRepo: "https://github.com/your-repo",
  //   metaDescription: "Your SEO description",
  //   keywords: "keyword1, keyword2",
  //   canonical: "https://yourportfolio.com/blog/your-post-slug",
  //   published: true,
  //   featured: false
  // },
];

// ============================================================
// COMPUTED PROPERTIES
// ============================================================

blogPosts.forEach((post) => {
  if (post.content && !post.readTime) {
    post.readTime = calculateReadingTime(post.content);
  }
  if (post.content && !post.excerpt) {
    post.excerpt = generateExcerpt(post.content);
  }
});

// ============================================================
// EXPORT FUNCTIONS
// ============================================================

export const getCategories = () => {
  const categoryMap = {};
  blogPosts.forEach((post) => {
    if (post.category) {
      categoryMap[post.category] = (categoryMap[post.category] || 0) + 1;
    }
  });
  return Object.keys(categoryMap);
};

export const getTags = () => {
  const tagMap = {};
  blogPosts.forEach((post) => {
    (post.tags || []).forEach((tag) => {
      tagMap[tag] = (tagMap[tag] || 0) + 1;
    });
  });
  return Object.keys(tagMap);
};

export const getCategoriesWithCounts = () => {
  const categoryMap = {};
  blogPosts.forEach((post) => {
    if (post.category) {
      categoryMap[post.category] = (categoryMap[post.category] || 0) + 1;
    }
  });
  return categoryMap;
};

export const getTagsWithCounts = () => {
  const tagMap = {};
  blogPosts.forEach((post) => {
    (post.tags || []).forEach((tag) => {
      tagMap[tag] = (tagMap[tag] || 0) + 1;
    });
  });
  return tagMap;
};

export const getPostsByCategory = (category) => {
  return blogPosts.filter((post) => post.category === category);
};

export const getPostsByTag = (tag) => {
  return blogPosts.filter((post) => (post.tags || []).includes(tag));
};

export const getFeaturedPost = () => {
  return blogPosts.find((post) => post.featured) || blogPosts[0];
};

export const getRecentPosts = (limit = 6) => {
  return [...blogPosts]
    .filter((post) => post.published !== false)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, limit);
};

export const getPopularPosts = (limit = 4) => {
  return [...blogPosts]
    .filter((post) => post.published !== false)
    .sort((a, b) => (b.likes || 0) - (a.likes || 0))
    .slice(0, limit);
};

export const getRelatedPosts = (post, limit = 3) => {
  if (!post) return [];

  return blogPosts
    .filter((p) => p.id !== post.id && p.published !== false)
    .map((p) => {
      let score = 0;
      if (p.category === post.category) score += 3;
      if (p.tags) {
        const sharedTags = p.tags.filter((tag) =>
          (post.tags || []).includes(tag),
        );
        score += sharedTags.length * 2;
      }
      return { ...p, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

export const getAdjacentPosts = (currentPost) => {
  if (!currentPost) return { prev: null, next: null };

  const sortedPosts = [...blogPosts]
    .filter((post) => post.published !== false)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const currentIndex = sortedPosts.findIndex((p) => p.id === currentPost.id);

  return {
    prev: currentIndex > 0 ? sortedPosts[currentIndex - 1] : null,
    next:
      currentIndex < sortedPosts.length - 1
        ? sortedPosts[currentIndex + 1]
        : null,
  };
};

export const searchPosts = (query, options = {}) => {
  const {
    categories = [],
    tags = [],
    limit = 20,
    sortBy = "date",
    publishedOnly = true,
  } = options;

  let results = [...blogPosts];

  if (publishedOnly) {
    results = results.filter((post) => post.published !== false);
  }

  if (query) {
    const searchTerm = query.toLowerCase().trim();
    results = results.filter(
      (post) =>
        post.title.toLowerCase().includes(searchTerm) ||
        post.excerpt.toLowerCase().includes(searchTerm) ||
        (post.tags || []).some((tag) =>
          tag.toLowerCase().includes(searchTerm),
        ) ||
        post.category.toLowerCase().includes(searchTerm),
    );
  }

  if (categories.length > 0) {
    results = results.filter((post) => categories.includes(post.category));
  }

  if (tags.length > 0) {
    results = results.filter((post) =>
      (post.tags || []).some((tag) => tags.includes(tag)),
    );
  }

  results.sort((a, b) => {
    if (sortBy === "date") {
      return new Date(b.date) - new Date(a.date);
    } else if (sortBy === "likes") {
      return (b.likes || 0) - (a.likes || 0);
    } else if (sortBy === "relevance") {
      if (query) {
        const term = query.toLowerCase().trim();
        const aScore = a.title.toLowerCase().includes(term)
          ? 3
          : a.excerpt.toLowerCase().includes(term)
            ? 2
            : 1;
        const bScore = b.title.toLowerCase().includes(term)
          ? 3
          : b.excerpt.toLowerCase().includes(term)
            ? 2
            : 1;
        return bScore - aScore;
      }
      return new Date(b.date) - new Date(a.date);
    }
    return 0;
  });

  return results.slice(0, limit);
};

export const getSitemapData = () => {
  return blogPosts
    .filter((post) => post.published !== false)
    .map((post) => ({
      url: `/blog/${post.slug}`,
      lastModified: post.date,
      changeFrequency: "weekly",
      priority: post.featured ? 1.0 : 0.7,
    }));
};

export const getRSSFeedData = () => {
  const baseUrl = "https://yourportfolio.com";
  return blogPosts
    .filter((post) => post.published !== false)
    .map((post) => ({
      title: post.title,
      link: `${baseUrl}/blog/${post.slug}`,
      description: post.excerpt,
      content: post.content,
      pubDate: new Date(post.date),
      author: post.author,
      categories: post.tags || [],
    }));
};

export const getBreadcrumbData = (post) => {
  const baseUrl = "https://yourportfolio.com";
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${baseUrl}/`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: `${baseUrl}/blog`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post?.title || "Article",
        item: post ? `${baseUrl}/blog/${post.slug}` : undefined,
      },
    ],
  };
};

export const getPostBySlug = (slug) => {
  return blogPosts.find((post) => post.slug === slug);
};

export const getPostById = (id) => {
  return blogPosts.find((post) => post.id === id);
};

// ============================================================
// EXPORT CONSTANTS
// ============================================================

export const CATEGORIES = getCategories();
export const TAGS = getTags();
export const CATEGORIES_WITH_COUNTS = getCategoriesWithCounts();
export const TAGS_WITH_COUNTS = getTagsWithCounts();
export const FEATURED_POST = getFeaturedPost();
export const RECENT_POSTS = getRecentPosts();
export const POPULAR_POSTS = getPopularPosts();

export default {
  blogPosts,
  CATEGORIES,
  TAGS,
  CATEGORIES_WITH_COUNTS,
  TAGS_WITH_COUNTS,
  FEATURED_POST,
  RECENT_POSTS,
  POPULAR_POSTS,
  getPostsByCategory,
  getPostsByTag,
  getFeaturedPost,
  getRecentPosts,
  getPopularPosts,
  getRelatedPosts,
  getAdjacentPosts,
  searchPosts,
  getSitemapData,
  getRSSFeedData,
  getBreadcrumbData,
  getPostBySlug,
  getPostById,
};
